import { getSelectedTimeZone } from '@/utils/dateTime';

// ── ZoomRange ─────────────────────────────────────────────────────────────────
export interface ZoomRange {
    start: number;
    end: number;
}

// ── Internal constants ────────────────────────────────────────────────────────
export const MARGIN_TOP = 20;
export const MARGIN_BOT = 44;
export const MARGIN_RIGHT = 14;
export const TICK_COUNT = 5;
export const MIN_X_LBL_PX = 52;
export const TOOLTIP_W = 184;
export const TOOLTIP_H_EST = 108;
export const ZOOM_FACTOR = 1.3;
export const MIN_ZOOM_WIN = 1;

// Section palettes — fallback order follows natural data hierarchy per domain
export const DEFAULT_COLORS: string[] = [
    '#3A7D99', // 1 — muted steel blue   — primary
    '#2E8870', // 2 — muted sage teal    — secondary
    '#6068A0', // 3 — muted slate indigo — tertiary
    '#607080', // 4 — cool gray          — neutral
    '#A04848', // 5 — muted dusty rose   — alert
    '#5898B4', // 6 — soft sky blue      — light primary
    '#50A890', // 7 — soft sage          — light secondary
    '#8888B0', // 8 — soft periwinkle    — light tertiary
];

// ── Pure math helpers ─────────────────────────────────────────────────────────
export function niceNum(v: number, round: boolean): number {
    if (v <= 0) return 0;
    const exp = Math.floor(Math.log10(v));
    const f = v / Math.pow(10, exp);
    let nf: number;
    if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
    else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nf * Math.pow(10, exp);
}

export function computeTicks(
    maxVal: number,
    count = TICK_COUNT,
): { ticks: number[]; niceMax: number } {
    if (maxVal <= 0) return { ticks: [0, 2, 4, 6, 8, 10], niceMax: 10 };
    const range = niceNum(maxVal * 1.08, false);
    const step = niceNum(range / (count - 1), true);
    const niceMax = Math.ceil(maxVal / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= niceMax + step * 0.001; v += step) ticks.push(+v.toFixed(10));
    return { ticks, niceMax };
}

export function fmtNum(v: number): string {
    if (!Number.isFinite(v)) return '—';
    if (v >= 1_000_000) return `${+(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${+(v / 1_000).toFixed(1)}K`;
    return String(v);
}

/** Build a smooth bezier curve path through an array of [x,y] points */
export function smoothBezier(pts: [number, number][], tension = 0.3): string {
    if (pts.length === 0) return '';
    if (pts.length === 1) return `M ${pts[0][0]} ${pts[0][1]}`;
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const [x0, y0] = pts[i];
        const [x1, y1] = pts[i + 1];
        const cpx1 = x0 + (x1 - x0) * tension;
        const cpx2 = x1 - (x1 - x0) * tension;
        d += ` C ${cpx1} ${y0},${cpx2} ${y1},${x1} ${y1}`;
    }
    return d;
}

/** SVG arc path for a pie/donut slice */
export function arcPath(
    cx: number,
    cy: number,
    outerR: number,
    innerR: number,
    startAngle: number,
    endAngle: number,
): string {
    // Clamp tiny slices to avoid degenerate arcs
    const span = Math.max(endAngle - startAngle, 0.0001);
    const sa = startAngle;
    const ea = startAngle + span;
    const cos = Math.cos;
    const sin = Math.sin;
    const large = span > Math.PI ? 1 : 0;

    const ox1 = cx + outerR * cos(sa);
    const oy1 = cy + outerR * sin(sa);
    const ox2 = cx + outerR * cos(ea);
    const oy2 = cy + outerR * sin(ea);

    if (innerR > 0) {
        const ix1 = cx + innerR * cos(ea);
        const iy1 = cy + innerR * sin(ea);
        const ix2 = cx + innerR * cos(sa);
        const iy2 = cy + innerR * sin(sa);
        return `M ${ox1} ${oy1} A ${outerR} ${outerR} 0 ${large} 1 ${ox2} ${oy2} L ${ix1} ${iy1} A ${innerR} ${innerR} 0 ${large} 0 ${ix2} ${iy2} Z`;
    }
    return `M ${cx} ${cy} L ${ox1} ${oy1} A ${outerR} ${outerR} 0 ${large} 1 ${ox2} ${oy2} Z`;
}

// ── Auto date-format helpers (used when no formatX / formatTooltipTitle provided) ──
export function _isDateLike(v: string | number): boolean {
    if (typeof v === 'number') return /^\d{10,13}$/.test(String(v));
    const s = String(v).trim();
    return /^\d{10,13}$/.test(s) || /^\d{4}-\d{2}-\d{2}/.test(s);
}

// Cache Intl.DateTimeFormat instances — creating them is expensive (locale/TZ lookup).
const _fmtCache = new Map<string, Intl.DateTimeFormat>();
function _getCachedFmt(tz: string, withYear: boolean): Intl.DateTimeFormat {
    const key = `${tz}:${withYear ? 'y' : 'n'}`;
    let fmt = _fmtCache.get(key);
    if (!fmt) {
        fmt = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            month: 'short',
            day: '2-digit',
            ...(withYear ? { year: 'numeric' } : {}),
        });
        _fmtCache.set(key, fmt);
    }
    return fmt;
}

export function _autoFmtShort(v: string | number): string {
    const raw = String(v).trim();
    // Unix timestamp
    if (/^\d{10,13}$/.test(raw)) {
        const n = Number(raw);
        const ms = n < 1_000_000_000_000 ? n * 1000 : n;
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, false);
    }
    // Date-only YYYY-MM-DD — avoid UTC-offset day-shift by parsing components
    const dm = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dm) {
        const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const mo = Number(dm[2]);
        return `${MONTHS[mo - 1]} ${dm[3]}`;
    }
    // ISO with time component
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, false);
    }
    return raw;
}

export function _autoFmtFull(v: string | number): string {
    const raw = String(v).trim();
    if (/^\d{10,13}$/.test(raw)) {
        const n = Number(raw);
        const ms = n < 1_000_000_000_000 ? n * 1000 : n;
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, true);
    }
    const dm = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dm) {
        const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const mo = Number(dm[2]);
        return `${MONTHS[mo - 1]} ${dm[3]}, ${dm[1]}`;
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, true);
    }
    return raw;
}

function _fmtDate(d: Date, withYear: boolean): string {
    try {
        return _getCachedFmt(getSelectedTimeZone(), withYear).format(d);
    } catch {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short', day: '2-digit',
            ...(withYear ? { year: 'numeric' } : {}),
        }).format(d);
    }
}
